import { Component } from '@theme/component';
import { releaseScrollLock } from '@theme/dialog';
import {
  showHeaderOverlayScrim,
  hideHeaderOverlayScrim,
  updateHeaderOverlayTop,
} from '@theme/header-overlay-scrim';

/**
 * Delay before a mega menu closes after the pointer leaves the header.
 * Gives people a forgiving diagonal path from the trigger down into the panel.
 */
const CLOSE_DELAY = 120;

/** Scroll distance after which the bar picks up its border and shadow. */
const SCROLL_THRESHOLD = 8;

/** Viewport width at which the drawer navigation gives way to the mega menu. */
const DESKTOP_BREAKPOINT = 990;

/**
 * The Zylia header: top level navigation, mega menu panels, the mobile menu
 * drawer and the scrolled state of the bar.
 *
 * Markup contract (see sections/zylia-custom-header.liquid):
 * - `.zylia-nav__item[data-mega-index]` — a top level item that owns a panel
 * - `.zylia-nav__link` — the trigger inside that item
 * - `.zylia-mega[data-mega-index]` — the panel, a sibling of the bar
 */
class ZyliaHeader extends Component {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  /** @type {string | null} */
  #openIndex = null;

  /** Whether this component currently holds a reference on the shared scrim. */
  #scrimShown = false;

  #scrollRaf = 0;

  #bound = false;

  /** @type {HTMLElement[]} */
  triggerItems = [];

  /** @type {HTMLElement[]} */
  panels = [];

  connectedCallback() {
    super.connectedCallback();

    this.#collect();
    this.#bind();
    this.#onScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#clearCloseTimer();
    this.#releaseScrim();

    this.removeEventListener('click', this.#handleHeaderClick);
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /** Re-read the DOM after a Section Rendering API morph. */
  updatedCallback() {
    super.updatedCallback?.();
    this.#collect();
  }

  #collect() {
    this.triggerItems = Array.from(this.querySelectorAll('.zylia-nav__item[data-mega-index]'));
    this.panels = Array.from(this.querySelectorAll('.zylia-mega'));
  }

  #bind() {
    if (this.#bound) return;
    this.#bound = true;

    // Capture, because pointerenter does not bubble but is dispatched on every
    // element the pointer enters — including the item we care about.
    this.addEventListener('pointerenter', this.#handlePointerEnter, true);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('click', this.#handleHeaderClick);

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize, { passive: true });
    document.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('click', this.#handleDocumentClick);
  }

  /* ------------------------------------------------------------------ *
   * Mega menu
   * ------------------------------------------------------------------ */

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    // Touch pointers fire enter on tap; let the tap navigate instead.
    if (event.pointerType === 'touch') return;
    if (this.#isDrawerMode()) return;
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.zylia-nav__item');
    const panel = event.target.closest('.zylia-mega');

    if (panel) {
      this.#clearCloseTimer();
      return;
    }

    if (!item) return;

    this.#clearCloseTimer();

    const index = item.getAttribute('data-mega-index');

    if (index) {
      this.open(index);
    } else {
      // A top level item without a panel closes whatever is open.
      this.close();
    }
  };

  /** @param {FocusEvent} event */
  #handleFocusIn = (event) => {
    if (this.#isDrawerMode()) return;
    if (!(event.target instanceof Element)) return;

    // Focus moving into an open panel must not close it.
    if (event.target.closest('.zylia-mega')) {
      this.#clearCloseTimer();
      return;
    }

    const item = event.target.closest('.zylia-nav__item');

    if (!item) {
      // Focus landed on the logo, search, account… — close the panel.
      this.close();
      return;
    }

    const index = item.getAttribute('data-mega-index');

    if (index) {
      this.#clearCloseTimer();
      this.open(index);
    } else {
      this.close();
    }
  };

  #handlePointerLeave = () => {
    this.scheduleClose();
  };

  /**
   * Opens the panel with the given index, closing any other.
   * @param {string} index
   */
  open(index) {
    if (this.#isDrawerMode()) return;
    if (this.#openIndex === index) return;

    this.#openIndex = index;

    for (const panel of this.panels) {
      const active = panel.getAttribute('data-mega-index') === index;
      panel.toggleAttribute('data-open', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    }

    for (const item of this.triggerItems) {
      const active = item.getAttribute('data-mega-index') === index;
      item.toggleAttribute('data-open', active);
      item.querySelector('.zylia-nav__link')?.setAttribute('aria-expanded', active ? 'true' : 'false');
    }

    this.setAttribute('data-mega-open', 'true');
    this.#claimScrim();
  }

  /** Closes any open panel immediately. */
  close = () => {
    this.#clearCloseTimer();

    if (!this.#openIndex) return;
    this.#openIndex = null;

    for (const panel of this.panels) {
      panel.removeAttribute('data-open');
      panel.setAttribute('aria-hidden', 'true');
    }

    for (const item of this.triggerItems) {
      item.removeAttribute('data-open');
      item.querySelector('.zylia-nav__link')?.setAttribute('aria-expanded', 'false');
    }

    this.removeAttribute('data-mega-open');
    this.#releaseScrim();
  };

  /** Closes after a short grace period, so crossing the gap does not flicker. */
  scheduleClose = () => {
    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(this.close, CLOSE_DELAY);
  };

  #clearCloseTimer() {
    if (this.#closeTimer == null) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }

  #isDrawerMode() {
    return window.innerWidth < DESKTOP_BREAKPOINT;
  }

  /* ------------------------------------------------------------------ *
   * Mobile drawer
   * ------------------------------------------------------------------ */

  /** Toggles the mobile navigation drawer. Bound from markup via `on:click`. */
  toggleDrawer = () => {
    if (this.hasAttribute('data-drawer-open')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  };

  openDrawer = () => {
    if (this.hasAttribute('data-drawer-open')) return;

    this.setAttribute('data-drawer-open', '');
    document.documentElement.setAttribute('scroll-lock', '');
    this.querySelector('.zylia-burger')?.setAttribute('aria-expanded', 'true');
    this.#claimScrim();

    const first = this.querySelector('.zylia-drawer__search, .zylia-drawer__tab, .zylia-drawer__panel a');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  };

  /**
   * @param {boolean} [restoreFocus] - Return focus to the burger. Pass false when
   *   another overlay (search, cart) is taking focus for itself.
   */
  closeDrawer = (restoreFocus = true) => {
    if (!this.hasAttribute('data-drawer-open')) return;

    this.removeAttribute('data-drawer-open');
    releaseScrollLock();

    const burger = this.querySelector('.zylia-burger');
    burger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && burger instanceof HTMLElement) burger.focus({ preventScroll: true });

    this.#releaseScrim();

    // Reopen in a known state rather than wherever the last visit left off.
    this.#restoreDefaultTab();
  };

  /**
   * Shows one drawer tab panel and hides the rest.
   * Bound from markup via `on:click="/selectTab/N"`.
   *
   * @param {string | number} index
   */
  selectTab = (index) => {
    const key = String(index);

    for (const tab of this.querySelectorAll('.zylia-drawer__tab[data-tab]')) {
      tab.setAttribute('aria-selected', tab.getAttribute('data-tab') === key ? 'true' : 'false');
    }

    for (const panel of this.querySelectorAll('.zylia-drawer__tabpanel')) {
      const active = panel.getAttribute('data-tab') === key;
      panel.toggleAttribute('hidden', !active);

      // Restart the enter animation so switching tabs reads as a change.
      if (active && panel instanceof HTMLElement) {
        panel.style.animation = 'none';
        void panel.offsetWidth;
        panel.style.animation = '';
      }
    }
  };

  /** Opens the shared search panel, putting the drawer away first. */
  openSearch = () => {
    this.closeDrawer(false);

    const searchPanel = /** @type {HTMLElement & { showDialog?: () => void }} */ (
      document.querySelector('#search-modal')
    );
    searchPanel?.showDialog?.();
  };

  #restoreDefaultTab() {
    const tabs = Array.from(this.querySelectorAll('.zylia-drawer__tab[data-tab]'));
    if (tabs.length === 0) return;

    const active = tabs.find((tab) => tab.getAttribute('data-default') === 'true') ?? tabs[0];
    const index = active?.getAttribute('data-tab');
    if (index != null) this.selectTab(index);
  }

  /* ------------------------------------------------------------------ *
   * Shared scrim — ref counted, so search and cart cannot fight over it
   * ------------------------------------------------------------------ */

  #claimScrim() {
    if (this.#scrimShown) {
      updateHeaderOverlayTop();
      return;
    }
    this.#scrimShown = true;
    showHeaderOverlayScrim();
  }

  #releaseScrim() {
    if (!this.#scrimShown) return;
    this.#scrimShown = false;
    hideHeaderOverlayScrim();
  }

  /* ------------------------------------------------------------------ *
   * Scrolled state
   * ------------------------------------------------------------------ */

  #handleScroll = () => {
    if (this.#scrollRaf) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = 0;
      this.#onScroll();
    });
  };

  #onScroll() {
    const scrolled = window.scrollY > SCROLL_THRESHOLD;
    if (scrolled === (this.dataset.scrolled === 'true')) return;
    this.dataset.scrolled = scrolled ? 'true' : 'false';
  }

  #handleResize = () => {
    updateHeaderOverlayTop();

    if (this.#isDrawerMode()) {
      this.close();
    } else {
      this.closeDrawer(false);
    }
  };

  /* ------------------------------------------------------------------ *
   * Dismissal
   * ------------------------------------------------------------------ */

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.hasAttribute('data-drawer-open')) {
      this.closeDrawer();
      return;
    }

    if (!this.#openIndex) return;

    // Grab the trigger before closing clears the attribute.
    const link = this.querySelector('.zylia-nav__item[data-open] .zylia-nav__link');
    this.close();
    if (link instanceof HTMLElement) link.focus({ preventScroll: true });
  };

  /**
   * The search panel and cart drawer are separate overlays that also live in the
   * header. Opening either of them should put the navigation away first, so the
   * page never ends up with two overlays and two scroll locks at once.
   *
   * @param {MouseEvent} event
   */
  #handleHeaderClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.header-actions__cart-toggle, search-button, .zylia-header__logo')) return;

    this.close();

    // The delegated `on:click` binding runs in the capture phase, so the search
    // panel has already opened by now — closing without restoring focus keeps
    // the caret in its input instead of snapping back to the burger.
    this.closeDrawer(false);
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (!this.#openIndex && !this.hasAttribute('data-drawer-open')) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.close();
    this.closeDrawer(false);
  };
}

if (!customElements.get('zylia-header')) customElements.define('zylia-header', ZyliaHeader);
